import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useInstagramConnection } from 'contexts/InstagramConnectionContext';
import { useTheme } from 'contexts/ThemeContext';

interface VerificationCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VerificationCodeModal: React.FC<VerificationCodeModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { connectionState, submitCode } = useInstagramConnection();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { isDark } = useTheme();

  // Reset code when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setLoading(false);
    }
  }, [isOpen]);

  // Close modal when connection step changes away from 'code'
  useEffect(() => {
    if (isOpen && connectionState.step !== 'code') {
      onClose();
    }
  }, [isOpen, connectionState.step, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    await submitCode(code);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const overlayBg = isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#314158';
  const emailColor = isDark ? '#51A2FF' : '#2B7FFF';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const closeButtonColor = isDark ? '#90A1B9' : '#314158';

  // Use the WebSocket message (connectionState.status) as description
  // Parse to extract email for highlighting if present
  const statusMessage = connectionState.status || '';
  const emailMatch = statusMessage.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
  const email = emailMatch ? emailMatch[1] : null;
  const descriptionText = statusMessage
    ? (email ? statusMessage.substring(0, statusMessage.indexOf(email)).trim() : statusMessage)
    : (t('addAccount.enterCodeDescription') || 'Enter the 6-digit code sent to your email address.');

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: overlayBg, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-[440px] mx-4 flex flex-col"
        style={{
          background: modalBg,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between"
          style={{
            padding: '24px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <h2
            className="text-xl font-bold leading-7"
            style={{ color: titleColor }}
          >
            {t('addAccount.authorizeAccess') || 'Authorize Instagram access'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0"
            style={{
              width: '28px',
              height: '28px',
              background: 'rgba(255, 255, 255, 0.01)',
              borderRadius: '6px',
              padding: '6px',
              color: closeButtonColor,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div
            className="flex flex-col"
            style={{ padding: '24px', gap: '24px', background: modalBg }}
          >
            {/* Description — two lines: text + email */}
            <div className="flex flex-col" style={{ gap: '4px' }}>
              <p
                className="font-normal leading-6"
                style={{ color: descriptionColor, fontSize: '16px' }}
              >
                {descriptionText}
              </p>
              {email && (
                <p
                  className="font-normal leading-6"
                  style={{ color: emailColor, fontSize: '16px' }}
                >
                  {email}
                </p>
              )}
            </div>

            {/* Input field container */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              {/* Label + Input */}
              <div className="flex flex-col" style={{ gap: '4px' }}>
                <label
                  className="text-sm font-medium leading-5"
                  style={{ color: labelColor }}
                >
                  {t('addAccount.verificationCodeLabel') || 'Verification code'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t('addAccount.enterVerificationCode') || 'Enter verification code'}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className="w-full text-sm outline-none transition-colors"
                  style={{
                    background: inputBg,
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    color: inputText,
                    padding: '8px 12px',
                    height: '36px',
                  }}
                />
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={code.length !== 6 || loading}
              className="w-full text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              style={{
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                borderRadius: '8px',
                height: '36px',
              }}
            >
              {loading
                ? (t('addAccount.submitting') || 'Submitting...')
                : (t('addAccount.authorizeAccessBtn') || 'Authorize access')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default VerificationCodeModal;
